import React, { useState } from 'react';
import { colorSet, theme, useSnackbar } from '@components/Provider';
import {
  CapaErrorCode,
  FetchMyPaymentDataByQuotationUserDocument,
  FetchQuotationUserRfqAvailableReviewsDocument,
  FetchQuotationUserRfqReviewsDocument,
  useUpdateRfqReviewMutation
} from '@generated/graphql';
import {
  Dialog,
  DialogActions,
  Grid,
  IconButton,
  DialogContent,
  Avatar,
  Divider,
  useMediaQuery
} from '@material-ui/core';
import { Close, OverflowMenuHorizontal } from '@carbon/icons-react';
import { CustomizedButton, CustomizedTypography } from '@components/Customized';
import RatingAndCommentBox from './RatingAndCommentBox';
import AttachPicturesBox from './AttachPicturesBox';
import useStyle from './styles';
import { useRegisterRfqReviewMutation } from '@generated/graphql';
import CheckDueDateBox from './CheckDueDateBox';
import { FormProvider, useForm } from 'react-hook-form';
import useReview from '@hooks/useReview';
import { getFileUrl } from '@utils/urls';
import { RfqReview } from '@modules/review/types';
import { useSelector } from '@store';
import LoadingPresenter from '@components/Loading';
const PROJECT_NAME_LENGTH_LIMIT = 20;

const ReviewWriteDialog: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const classes = useStyle();
  const { enqueueSnackbar } = useSnackbar();
  const { onInitReview } = useReview();
  const id = useSelector((state) => state.client.id);
  const review = useSelector((state) => state.review);
  const methods = useForm<RfqReview>();
  const { handleSubmit } = methods;
  const { open, reviewId, projectName } = review;
  const hasReview = reviewId !== '';

  const handleClose = () => {
    onInitReview();
  };

  const [registerRfqReviewMutation] = useRegisterRfqReviewMutation({
    refetchQueries: [
      {
        query: FetchQuotationUserRfqAvailableReviewsDocument,
        variables: {
          input: {
            quotationUserId: id
          }
        },
        fetchPolicy: 'network-only'
      },
      {
        query: FetchQuotationUserRfqReviewsDocument,
        variables: {
          input: {
            quotationUserId: id
          }
        },
        fetchPolicy: 'network-only'
      },
      {
        query: FetchMyPaymentDataByQuotationUserDocument,
        variables: { input: {} }
      }
    ],
    awaitRefetchQueries: true
  });
  const [updateRfqReviewMutation] = useUpdateRfqReviewMutation({
    refetchQueries: [
      {
        query: FetchQuotationUserRfqAvailableReviewsDocument,
        variables: {
          input: {
            quotationUserId: id
          }
        },
        fetchPolicy: 'network-only'
      },
      {
        query: FetchQuotationUserRfqReviewsDocument,
        variables: {
          input: {
            quotationUserId: id
          }
        },
        fetchPolicy: 'network-only'
      }
    ],
    awaitRefetchQueries: true
  });

  const onSubmit = (params: RfqReview) => {
    setLoading(true);

    if (hasReview) {
      updateRfqReviewMutation({
        variables: {
          input: {
            reviewId: review.reviewId,
            rfqReviewType: review.rfqReviewType,
            transactionSatisfactionPoint: params.transactionSatisfactionPoint,
            transactionSatisfactionComment:
              params.transactionSatisfactionComment,
            reviewFiles: params.reviewFiles,
            inDueDate: params.inDueDate
          }
        }
      })
        .then((result) => {
          switch (result.data?.updateRfqReview.result) {
            case CapaErrorCode.Success:
              enqueueSnackbar('리뷰가 수정되었습니다.', {
                variant: 'success'
              });
              break;
            case (CapaErrorCode.TransactionReviewTimeExceed,
            CapaErrorCode.TransactionReviewTimeExceed):
              enqueueSnackbar('리뷰 수정 가능 시간이 초과되었습니다.', {
                variant: 'error'
              });
              break;
            default:
              throw new Error(`${result.data?.updateRfqReview?.result}`);
          }
        })
        .catch((err) => {
          enqueueSnackbar('리뷰가 수정이 실패했습니다.', {
            variant: 'error'
          });
        })
        .finally(() => {
          setLoading(false);
          handleClose();
        });

      return;
    }

    registerRfqReviewMutation({
      variables: {
        input: {
          rfqId: review.rfqId,
          quotationPartnerId: review.quotationPartnerId,
          rfqReviewType: params.rfqReviewType,
          transactionSatisfactionPoint: params.transactionSatisfactionPoint,
          transactionSatisfactionComment: params.transactionSatisfactionComment,
          reviewFiles: params.reviewFiles,
          inDueDate: params.inDueDate
        }
      }
    })
      .then((result) => {
        if (result.data?.registerRfqReview.result === CapaErrorCode.Success) {
          enqueueSnackbar('리뷰가 작성되었습니다.', {
            variant: 'success'
          });
        } else {
          throw new Error(`${result.data?.registerRfqReview?.result}`);
        }
      })
      .catch((err) => {
        enqueueSnackbar('리뷰 작성이 실패했습니다.', {
          variant: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
        handleClose();
      });
  };

  const matches = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <FormProvider {...methods}>
      {loading && <LoadingPresenter open />}
      <form>
        <Dialog
          fullScreen={matches}
          onClose={handleClose}
          open={open}
          classes={{ paper: !matches ? classes.dialogPaper : undefined }}
          fullWidth
          scroll="paper"
        >
          <Grid container className={classes.header} alignItems="center">
            <Grid item>
              <CustomizedTypography
                variant="h4"
                fontWeight="700"
                color="textPrimary"
              >
                리뷰 {hasReview ? '수정' : '작성'}
              </CustomizedTypography>
            </Grid>
            <IconButton className={classes.iconButton} onClick={handleClose}>
              <Close size={24} color={colorSet.gray900} />
            </IconButton>
          </Grid>
          <Grid container direction="row" className={classes.partnerInfo}>
            <Grid item>
              <Avatar alt="profile" src={getFileUrl(review.profileId ?? '')} />
            </Grid>
            <Grid item>
              <CustomizedTypography
                variant="body2"
                color="textPrimary"
                fontWeight="500"
              >
                {projectName.length > PROJECT_NAME_LENGTH_LIMIT ? (
                  <>
                    {projectName.substring(0, PROJECT_NAME_LENGTH_LIMIT)}
                    <OverflowMenuHorizontal />
                  </>
                ) : (
                  projectName
                )}
              </CustomizedTypography>
              <CustomizedTypography variant="subtitle2" color="textSecondary">
                {review.organizationName}
              </CustomizedTypography>
            </Grid>
          </Grid>
          <Divider className={classes.divider} />
          <DialogContent dividers className={classes.content}>
            <RatingAndCommentBox
              title="받아보신 제품의 품질은 어떠셨나요?"
              subTitle="제품 품질에 대해 적어주세요."
              goodRatingTitle="파트너의 좋았던 점을 알려주세요."
              badRatingTitle="파트너가 무엇을 개선하면 좋을까요?"
              isRequired
            />
            <AttachPicturesBox />
            <CheckDueDateBox />
          </DialogContent>
          <DialogActions className={classes.footer}>
            <CustomizedButton
              color="primary"
              className="button"
              onClick={handleSubmit(onSubmit)}
            >
              리뷰 등록하기
            </CustomizedButton>
          </DialogActions>
        </Dialog>
      </form>
    </FormProvider>
  );
};

export default ReviewWriteDialog;
